import { useMemo } from "react";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import type { CalendarEvent } from "@/api/calendar";
import { DayCell } from "./DayCell";

interface MonthViewProps {
  /** Anchor date — any day inside the month being rendered. */
  anchor: Date;
  events: CalendarEvent[];
  loading?: boolean;
  onCreateAt: (date: Date) => void;
  onOpenEvent: (event: CalendarEvent) => void;
  isDone: (key: string | null | undefined) => boolean;
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function MonthView({
  anchor,
  events,
  loading,
  onCreateAt,
  onOpenEvent,
  isDone,
}: MonthViewProps) {
  // Build the 6×7 grid: from the Monday of the week containing the
  // first of the month, to the Sunday of the week containing the last.
  const days = useMemo(() => {
    const monthStart = startOfMonth(anchor);
    const monthEnd = endOfMonth(anchor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const out: Date[] = [];
    let cursor = gridStart;
    while (cursor <= gridEnd) {
      out.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return out;
  }, [anchor]);

  const monthIndex = anchor.getMonth();

  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      <div className="grid grid-cols-7 border-b border-slate-900/[0.06] bg-slate-50">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="px-2 py-2 text-[11px] font-medium uppercase tracking-wider text-slate-500"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="relative grid grid-cols-7">
        {days.map((d) => (
          <DayCell
            key={d.toISOString()}
            date={d}
            events={events}
            inCurrentMonth={d.getMonth() === monthIndex}
            onCreateAt={onCreateAt}
            onOpenEvent={onOpenEvent}
            isDone={isDone}
          />
        ))}
        {loading && (
          <div className="pointer-events-none absolute inset-0 bg-white/40" />
        )}
      </div>
      <span className="sr-only" aria-live="polite">
        Showing {format(anchor, "MMMM yyyy")}
      </span>
    </div>
  );
}
